/**
 * Jetons + et − — logique pure (Boîte à outils, 5e → 3e).
 *
 * Un jeton + vaut +1, un jeton − vaut −1 : une paire (+1 ; −1) vaut 0.
 * Additionner = poser les jetons puis retirer les paires ; soustraire =
 * enlever des jetons (on ajoute d'abord des paires nulles s'il en manque) ;
 * multiplier par un négatif = enlever des groupes à partir de zéro. Chaque
 * calcul est une suite de scènes (état des jetons + phrase) que l'outil
 * déroule étape par étape.
 */

export type Sign = 1 | -1;

/** état des jetons pour la figure en paires (une rangée +, la rangée − dessous) */
export interface PairScene {
  pos: number;
  neg: number;
  /** les `pairs` premières paires (+1 ; −1) sont entourées */
  pairs: number;
  /** paires effacées (elles valent 0) */
  cancelled: boolean;
  /** les `added` derniers jetons de chaque signe viennent d'être ajoutés */
  added: number;
  /** derniers jetons retirés */
  removedPos: number;
  removedNeg: number;
  /**
   * décalage (en colonnes) des jetons d'un signe : les paires ajoutées se
   * placent sous les jetons + ajoutés, pas sous ceux du départ
   */
  posOffset: number;
  negOffset: number;
  say: string;
}

/** colonnes occupées par une scène */
export const sceneWidth = (s: PairScene) => Math.max(s.pos + s.posOffset, s.neg + s.negOffset);

export interface Block {
  sign: Sign;
  rows: number;
  cols: number;
  removed: boolean;
  added: boolean;
}

export interface BlockScene {
  blocks: Block[];
  say: string;
}

const MINUS = '−';

/** « +5 », « −3 », « 0 » */
export function signed(n: number): string {
  if (n === 0) return '0';
  return `${n > 0 ? '+' : MINUS}${fmt(Math.abs(n))}`;
}

/** « (+5) », « (−3) », « 0 » */
export const rel = (n: number) => (n === 0 ? '0' : `(${signed(n)})`);

/** en LaTeX : « (+5) », « (-3) » ; décimaux « 2{,}5 » */
export function relTex(n: number): string {
  if (n === 0) return '0';
  return `(${n > 0 ? '+' : '-'}${fmt(Math.abs(n)).replace(',', '{,}')})`;
}
export function signedTex(n: number): string {
  if (n === 0) return '0';
  return `${n > 0 ? '+' : '-'}${fmt(Math.abs(n)).replace(',', '{,}')}`;
}

/** 2,5 et pas 2.5 */
export const fmt = (v: number) => String(Number(v.toFixed(2))).replace('.', ',');

const tokens = (k: number, sign: Sign) => `${k} jeton${k > 1 ? 's' : ''} ${sign > 0 ? '+' : MINUS}`;

/** les mots pour le lecteur d'écran : « plus 5 », « moins 3 » */
export function spoken(n: number): string {
  if (n === 0) return '0';
  return `${n > 0 ? 'plus' : 'moins'} ${fmt(Math.abs(n))}`;
}

/* ------------------------------------------------------------------ */
/* somme                                                               */
/* ------------------------------------------------------------------ */

export function sumScenes(terms: readonly number[]): {
  result: number;
  scenes: PairScene[];
  pos: number;
  neg: number;
} {
  const pos = terms.filter((t) => t > 0).reduce((s, t) => s + t, 0);
  const neg = -terms.filter((t) => t < 0).reduce((s, t) => s + t, 0);
  const result = pos - neg;
  const base = {
    pos,
    neg,
    pairs: 0,
    cancelled: false,
    added: 0,
    removedPos: 0,
    removedNeg: 0,
    posOffset: 0,
    negOffset: 0,
  };
  const listed = terms
    .filter((t) => t !== 0)
    .map((t) => `${rel(t)} : ${tokens(Math.abs(t), t > 0 ? 1 : -1)}`);
  const scenes: PairScene[] = [
    { ...base, say: listed.length ? `${listed.join(' ; ')}.` : 'Aucun jeton.' },
  ];
  const p = Math.min(pos, neg);
  if (p > 0) {
    scenes.push({
      ...base,
      pairs: p,
      say: `${p} paire${p > 1 ? 's' : ''} (+1 ; −1) : chacune vaut 0.`,
    });
    scenes.push({ ...base, pairs: p, cancelled: true, say: leftover(result) });
  } else {
    scenes.push({
      ...base,
      say:
        pos + neg === 0
          ? 'Aucun jeton : la somme vaut 0.'
          : `Aucune paire (+1 ; −1) à retirer : ${tokens(pos + neg, pos > 0 ? 1 : -1)} en tout, la somme vaut ${signed(result)}.`,
    });
  }
  return { result, scenes, pos, neg };
}

function leftover(result: number): string {
  if (result === 0) return 'Tout s’annule : il ne reste aucun jeton, la somme vaut 0.';
  return `Il reste ${tokens(Math.abs(result), result > 0 ? 1 : -1)} : la somme vaut ${signed(result)}.`;
}

/** (+5) + (−3) + (−7) + (+2) = (+7) + (−10) = −3 (dès trois termes, avec des deux signes) */
export function regroupTex(terms: readonly number[]): string | null {
  const pos = terms.filter((t) => t > 0);
  const neg = terms.filter((t) => t < 0);
  if (terms.length < 3 || pos.length === 0 || neg.length === 0) return null;
  const sp = pos.reduce((s, t) => s + t, 0);
  const sn = neg.reduce((s, t) => s + t, 0);
  return `${terms.map(relTex).join(' + ')} = ${relTex(sp)} + ${relTex(sn)} = ${signedTex(sp + sn)}`;
}

/** écriture simplifiée : 5 − 3 − 7 + 2 */
export function simplifiedTex(terms: readonly number[]): string {
  const nz = terms.filter((t) => t !== 0);
  if (nz.length === 0) return '0';
  return nz
    .map((t, i) => {
      const a = fmt(Math.abs(t)).replace(',', '{,}');
      if (i === 0) return t < 0 ? `-${a}` : a;
      return `${t < 0 ? '-' : '+'} ${a}`;
    })
    .join(' ');
}

/* ------------------------------------------------------------------ */
/* différence                                                          */
/* ------------------------------------------------------------------ */

export function subScenes(a: number, b: number): { result: number; scenes: PairScene[] } {
  const result = a - b;
  let pos = Math.max(a, 0);
  let neg = Math.max(-a, 0);
  const base = {
    pairs: 0,
    cancelled: false,
    added: 0,
    removedPos: 0,
    removedNeg: 0,
    posOffset: 0,
    negOffset: 0,
  };
  const scenes: PairScene[] = [
    {
      ...base,
      pos,
      neg,
      say:
        a === 0
          ? 'On part de 0 : aucun jeton.'
          : `${rel(a)} : ${tokens(Math.abs(a), a > 0 ? 1 : -1)}.`,
    },
  ];
  if (b === 0) {
    scenes.push({
      ...base,
      pos,
      neg,
      say: `Enlever 0, c’est ne rien enlever : ${signed(result)}.`,
    });
    return { result, scenes };
  }
  const sb: Sign = b > 0 ? 1 : -1;
  const k = Math.abs(b);
  const have = sb > 0 ? pos : neg;
  if (have < k) {
    const add = k - have;
    pos += add;
    neg += add;
    // les jetons de départ sont d'un seul signe : l'autre rangée commence sous les ajoutés
    if (a > 0) base.negOffset = a;
    else if (a < 0) base.posOffset = -a;
    scenes.push({
      ...base,
      pos,
      neg,
      added: add,
      say: `Pour enlever ${tokens(k, sb)}, il en manque ${add} : on ajoute ${add} paire${add > 1 ? 's' : ''} (+1 ; −1), ce qui ne change pas la valeur.`,
    });
  }
  const removed = { removedPos: sb > 0 ? k : 0, removedNeg: sb < 0 ? k : 0 };
  scenes.push({
    ...base,
    pos,
    neg,
    ...removed,
    say: `On enlève ${tokens(k, sb)}.`,
  });
  scenes.push({
    ...base,
    pos: pos - removed.removedPos,
    neg: neg - removed.removedNeg,
    say: `${leftover(result).replace('la somme', 'la différence')} Enlever ${tokens(k, sb)}, c’est comme ajouter ${tokens(k, sb > 0 ? -1 : 1)} : on ajoute l’opposé.`,
  });
  return { result, scenes };
}

/** (+2) − (−3) = (+2) + (+3) = +5 */
export function oppositeTex(a: number, b: number): string {
  return `${relTex(a)} - ${relTex(b)} = ${relTex(a)} + ${relTex(-b)} = ${signedTex(a - b)}`;
}

/* ------------------------------------------------------------------ */
/* produit                                                             */
/* ------------------------------------------------------------------ */

export function productScenes(a: number, b: number): { result: number; scenes: BlockScene[] } {
  const result = a * b + 0; // jamais −0
  if (a === 0 || b === 0) {
    return {
      result,
      scenes: [{ blocks: [], say: 'Un facteur est nul : aucun jeton, le produit vaut 0.' }],
    };
  }
  const sb: Sign = b > 0 ? 1 : -1;
  const rows = Math.abs(a);
  const cols = Math.abs(b);
  const block = (sign: Sign, extra: Partial<Block> = {}): Block => ({
    sign,
    rows,
    cols,
    removed: false,
    added: false,
    ...extra,
  });
  const end = `${rel(a)} × ${rel(b)} = ${signed(result)}`;
  if (a > 0) {
    return {
      result,
      scenes: [
        {
          blocks: [block(sb)],
          say: `${a} fois ${rel(b)} : on pose ${a} groupe${a > 1 ? 's' : ''} de ${tokens(cols, sb)}.`,
        },
        {
          blocks: [block(sb)],
          say: `${tokens(rows * cols, sb)} en tout : ${end}.`,
        },
      ],
    };
  }
  const n = rows * cols;
  return {
    result,
    scenes: [
      {
        blocks: [block(1, { added: true }), block(-1, { added: true })],
        say:
          n > 1
            ? `On part de zéro avec ${n} paires (+1 ; −1) : elles valent 0.`
            : 'On part de zéro avec une paire (+1 ; −1) : elle vaut 0.',
      },
      {
        blocks: [block(1, { removed: sb > 0 }), block(-1, { removed: sb < 0 })],
        say: `${rel(a)} fois, c’est enlever ${rows} groupe${rows > 1 ? 's' : ''} de ${tokens(cols, sb)}.`,
      },
      {
        blocks: [block(sb > 0 ? -1 : 1)],
        say: `Il reste ${tokens(n, sb > 0 ? -1 : 1)} : ${end}.`,
      },
    ],
  };
}

/** signe d'un produit de facteurs non nuls : on compte les facteurs négatifs */
export function productSign(signs: readonly Sign[]): {
  negatives: number;
  sign: Sign;
  say: string;
} {
  const negatives = signs.filter((s) => s < 0).length;
  const sign: Sign = negatives % 2 === 0 ? 1 : -1;
  const count =
    negatives === 0
      ? 'aucun facteur négatif'
      : `${negatives} facteur${negatives > 1 ? 's' : ''} négatif${negatives > 1 ? 's' : ''}`;
  return {
    negatives,
    sign,
    say:
      negatives === 0
        ? 'Aucun facteur négatif : le produit est positif.'
        : `${count[0]!.toUpperCase()}${count.slice(1)}, un nombre ${negatives % 2 === 0 ? 'pair' : 'impair'} : le produit est ${sign > 0 ? 'positif' : 'négatif'}.`,
  };
}

/* ------------------------------------------------------------------ */
/* droite graduée                                                      */
/* ------------------------------------------------------------------ */

export function compareRelatives(a: number, b: number): { rel: '<' | '=' | '>'; say: string } {
  const r = a < b ? '<' : a > b ? '>' : '=';
  const [lo, hi] = a <= b ? [a, b] : [b, a];
  if (r === '=') return { rel: r, say: 'Même point de la droite : les deux nombres sont égaux.' };
  let say: string;
  if (lo === 0) say = '0 est plus petit que tout nombre positif.';
  else if (lo > 0)
    say = `Deux nombres positifs : ${signed(hi)} est plus loin de zéro, donc plus à droite.`;
  else if (hi < 0)
    say = `Deux nombres négatifs : ${signed(lo)} est plus loin de zéro, donc plus à gauche.`;
  else if (hi === 0) say = 'Un nombre négatif est plus petit que 0.';
  else say = 'Un nombre négatif est toujours plus petit qu’un nombre positif.';
  return { rel: r, say: `${say} Le plus petit est à gauche : ${signed(lo)} < ${signed(hi)}.` };
}
